import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Flex,
    Popconfirm,
    Tag,
    Space,
    Tooltip
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import FilterBar from 'components/Common/FilterBar';
import PageActions from 'components/Common/PageActions';
import useUrlFilters from 'hooks/useUrlFilters';

// Search and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
    search: { type: 'string' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 50 },
};

const LinkCategoriesTable = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);

    // State
    const searchTerm = filters.search || '';
    const setSearchTerm = (value) => setFilters({ search: value, page: 1 });
    const [searchTermLive, setSearchTermLive] = useState(searchTerm);
    // Keep the box in step when the URL changes underneath it (back/forward).
    useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

    const currentPage = filters.page;
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const [totalRows, setTotalRows] = useState(0);

    const [allCategories, setAllCategories] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAllCategories();
    }, [searchTerm]);

    useEffect(() => {
        paginateData();
    }, [allCategories, currentPage, pageSize]);

    const fetchAllCategories = async () => {
        setLoading(true);
        try {
            const response = await actions.loadLinkCategories(1, searchTerm, 1000);
            if (response.status === 200) {
                const data = response.data;
                let results = data.results || data || [];
                setAllCategories(results);
                setTotalRows(results.length);
            }
        } catch (e) {
            console.error('Error fetching categories:', e);
        }
        setLoading(false);
    };

    const paginateData = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = allCategories.slice(startIndex, endIndex);
        setDataSource(paginatedData);
    };

    const onSearch = (value) => {
        setSearchTerm(value);
    };


    const handleDelete = async (id) => {
        const response = await actions.deleteLinkCategory(id);
        if (response.status === 204) {
            fetchAllCategories();
        }
    };

    const columns = useMemo(() => [
        {
            title: 'NAME',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a?.name?.localeCompare(b?.name),
            width: 200,
        },
        {
            title: 'SLUG',
            dataIndex: 'slug',
            key: 'slug',
            width: 150,
        },
        {
            title: 'COLOR',
            dataIndex: 'color',
            key: 'color',
            width: 100,
            render: (color) => (
                <Space>
                    <div
                        style={{
                            width: 20,
                            height: 20,
                            backgroundColor: color || '#6366f1',
                            borderRadius: 4,
                            border: '1px solid #d9d9d9'
                        }}
                    />
                    <span>{color}</span>
                </Space>
            ),
        },
        {
            title: 'ICON',
            dataIndex: 'icon',
            key: 'icon',
            width: 100,
            render: (icon) => icon || '-',
        },
        {
            title: 'ORDER',
            dataIndex: 'order',
            key: 'order',
            width: 80,
            sorter: (a, b) => a.order - b.order,
        },
        {
            title: 'ACTIVE',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 80,
            render: (isActive) => (
                isActive ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag>
                ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>No</Tag>
                )
            ),
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            width: 250,
            ellipsis: true,
            render: (desc) => desc || '-',
        },
        {
            title: 'ACTIONS',
            dataIndex: 'actions',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Flex gap="small">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/links/categories/edit/${record.id}`)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Sure to delete?"
                        description="This will also remove the category from all associated links."
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Tooltip title="Delete">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Flex>
            ),
        },
    ], [navigate]);

    return (
        <div style={{ padding: 10 }}>
            <div style={{ marginBottom: 8 }}>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} categories
      </div>
            <Flex gap="large" justify="space-between" align="flex-start" wrap>
                <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
                        searchValue={searchTermLive}
                        onSearchChange={setSearchTermLive}
                        onSearch={onSearch}
                        searchPlaceholder="Search categories"
                        onRefresh={() => fetchAllCategories()}
                        refreshing={loading}
                    />
                </div>
                <PageActions
                    primary={{
                        label: 'Add Category',
                        shortLabel: 'Add',
                        icon: <PlusOutlined />,
                        onClick: () => navigate('/links/categories/new'),
                    }}
                    actions={[
                        {
                            key: 'back',
                            label: 'Back to Links',
                            icon: <ArrowLeftOutlined />,
                            onClick: () => navigate('/links'),
                        },
                    ]}
                />
            </Flex>

            <div style={{ marginTop: 20 }}>
                <Table
                    loading={loading}
                    bordered
                    size="small"
                    dataSource={dataSource}
                    columns={columns}
                    rowKey={row => row.id}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRows,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        onChange: (page, size) => {
                            setFilters({ page: size !== pageSize ? 1 : page, pageSize: size });
                        },
                    }}
                    scroll={{
                        x: 'max-content',
                        y: 'calc(100vh - 350px)',
                    }}
                    sticky
                />
            </div>
        </div>
    );
};

export default LinkCategoriesTable;
